export const TOKEN_STORAGE_KEY = 'token';

/**
 * Every token the app stores is a JWT (Auth0 id token, SSO id token, Outlook
 * add-in token): three non-empty dot-separated segments. Anything else is
 * junk (`localStorage.token = undefined` stores the string 'undefined') or a
 * truncated `?accessToken=`, and the API rejects it as a malformed token
 * (400 IDX12709) instead of answering the regular 401.
 */
export const isJwtShaped = (value: unknown): value is string => {
  if (typeof value !== 'string') return false;

  const segments = value.split('.');
  return segments.length === 3 && segments.every(segment => segment.length > 0);
};

/** The stored session token, or null when there is none or it is unusable. */
export const getStoredToken = (): string | null => {
  try {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    return isJwtShaped(token) ? token : null;
  } catch {
    // Storage access can throw (disabled storage, sandboxed iframe).
    return null;
  }
};

// base64url → UTF-8 text. `atob` yields one char per byte, so the bytes are
// percent-encoded and decoded as UTF-8 (no `TextDecoder` dependency).
const decodeBase64Url = (segment: string) => {
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const bytes = Array.from(
    atob(padded),
    char => `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`
  );

  return decodeURIComponent(bytes.join(''));
};

/**
 * The `sub` claim (the identity provider's user id), or null when the token
 * cannot be read. Only identifies who the token is for; it does not verify it.
 */
export const getJwtSubject = (token: unknown): string | null => {
  if (!isJwtShaped(token)) return null;

  try {
    const payload = JSON.parse(decodeBase64Url(token.split('.')[1]));
    return typeof payload?.sub === 'string' && payload.sub ? payload.sub : null;
  } catch {
    return null;
  }
};
